import React, { useState } from 'react'
import { gql, useMutation } from 'urql'
import { AlertSeverity, ContactMethodType, WeekdayFilter } from '../../schema'
import FormDialog from '../dialogs/FormDialog'
import { fieldErrors, nonFieldErrors } from '../util/errutil'
import ServiceNotificationRuleForm from './ServiceNotificationRuleForm'
import { DateTime } from 'luxon'

const mutation = gql`
  mutation ($input: CreateServiceNotificationRuleInput!) {
    createServiceNotificationRule(input: $input) {
      id
    }
  }
`

interface ServiceNotificationRuleCreateDialogProps {
  serviceID: string
  onClose: () => void
  onSubmit?: (rule: {
    contactMethodType: ContactMethodType
    alertSeverity: AlertSeverity
    delayMinutes: number
    notifyAnytime: boolean
    startTime: string
    endTime: string
    timezone: string
    weekdayFilter: WeekdayFilter
  }) => void
}

export default function ServiceNotificationRuleCreateDialog(
  props: ServiceNotificationRuleCreateDialogProps,
): JSX.Element {
  const [value, setValue] = useState({
    contactMethodType: 'VOICE' as ContactMethodType,
    alertSeverity: 'MEDIUM' as AlertSeverity,
    delayMinutes: 0,
    notifyAnytime: true,
    startTime: DateTime.local().startOf('day').toUTC().toISO(),
    endTime: DateTime.local().plus({ day: 1 }).startOf('day').toUTC().toISO(),
    timezone: DateTime.local().zoneName,
    weekdayFilter: new Array(7).fill(true) as WeekdayFilter,
  })

  const [{ fetching, error }, createNotification] = useMutation(mutation)

  const fieldErrs = fieldErrors(error)

  const handleSubmit = (): void => {
    if (props.onSubmit) {
      props.onSubmit(value)
      props.onClose()
      return
    }

    const input = {
      serviceID: props.serviceID,
      contactMethodType: value.contactMethodType,
      alertSeverity: value.alertSeverity,
      delayMinutes: value.delayMinutes,
      notifyAnytime: value.notifyAnytime ?? true,
      startTime: DateTime.fromISO(value.startTime).toFormat('HH:mm'),
      endTime: DateTime.fromISO(value.endTime).toFormat('HH:mm'),
      timezone: value.timezone,
      weekdayFilter: value.weekdayFilter,
    }

    createNotification(
      {
        input,
      },
      {
        additionalTypenames: ['ServiceNotificationRule', 'Service'],
      },
    ).then((result) => {
      if (!result.error) props.onClose()
    })
  }

  return (
    <FormDialog
      title='Create New Notification Rule'
      loading={fetching}
      errors={nonFieldErrors(error)}
      onClose={props.onClose}
      onSubmit={handleSubmit}
      form={
        <ServiceNotificationRuleForm
          errors={fieldErrs}
          disabled={fetching}
          value={value}
          onChange={(value) => setValue(value)}
        />
      }
    />
  )
}
